import { computed, defineComponent, onMounted, onUnmounted, PropType, ref } from "vue";
import { Product } from "../../interfaces/Product";
import store from '@/store';
import { getBrandSeoUrl } from "@/services/productService";

const pendingRequests: Record<string, Promise<string>> = {}; // Cache per le richieste in corso

export default defineComponent({
	name: "ProductTile",
	props: {
		product: {
			type: Object as PropType<Product>,
			required: true,
		},
		index: {
			type: Number,
			required: true,
		},
		isLargeTile: {
			type: Boolean,
			required: true,
		},
		isMobile: {
			type: Boolean,
			required: true,
		}
	},
	setup(props) {
		const productUrl = ref(`https://${window.location.hostname}/gl-us/eyewear/${props.product.partNumber}`);
		const secondImageLoaded = ref(false); // Stato per controllare il caricamento dell'immagine secondari

		function getFirstImage(firstImgUrl: string) {
			return `${firstImgUrl}?impolicy=GL_g-thumbnail-plp`;
		}

		const getSecondImage = (secondImgUrl: string) => {
			return `${secondImgUrl}?impolicy=GL_g-thumbnail-plp`;
		};

		// Funzione per caricare l'immagine secondaria al passaggio del mouse
		const loadSecondImage = () => {
			if (!secondImageLoaded.value) {
				secondImageLoaded.value = true; // Imposta lo stato a true per caricare l'immagine
			}
		};

		const firstImageRemoveLoader = ref(false); // Stato per il caricamento della prima immagine
		const secondImageRemoveLoader = ref(false); // Stato per il caricamento della seconda immagine
		const bothImagesLoaded = ref(false);

		const hasAlternateTransition = computed(() =>
			props.product.secondImgUrl?.includes('_al')
		);

		// Funzione chiamata al caricamento della prima immagine
		const onFirstImageRemoveLoader = () => {
			firstImageRemoveLoader.value = true;
			checkImagesLoaded();
		};

		// Funzione chiamata al caricamento della seconda immagine
		const onSecondImageRemoveLoader = () => {
			secondImageRemoveLoader.value = true;
			checkImagesLoaded();
		};

		const checkImagesLoaded = () => {
			if (firstImageRemoveLoader.value && secondImageRemoveLoader.value) {
				bothImagesLoaded.value = true;
			}
		};

		const discountPercentage = computed(() =>
			calculateDiscountPercentage(props.product.listPrice, props.product.offerPrice)
		);



		const calculateDiscountPercentage = (listPrice: string, offerPrice: string): number => {
			const listPriceNum = Number(listPrice);
			const offerPriceNum = Number(offerPrice);

			if (listPriceNum > offerPriceNum && offerPriceNum > 0) {
				const discount = ((listPriceNum - offerPriceNum) * 100) / listPriceNum;
				return Math.round(discount);
			}

			return 0;
		};

		function getModelName() {
			if (props.product.productAttributes.get('BADGE_CHANNEL')?.toLowerCase() != null && props.product.productAttributes.get('BADGE_CHANNEL')?.trim().length != 0) {
				return props.product.productAttributes.get('MODEL_NAME') + " " + props.product.productAttributes.get('BADGE_CHANNEL')?.replace("| Meta ", "");
			} else {
				return props.product.productAttributes.get('MODEL_NAME');
			}
		};

		const getFirstBadge = () => {
			if (props.product.productAttributes.get('MACRO_AGE_RANGE')?.toLowerCase() == "children") {
				return "Kids";
			} else if (props.product.productAttributes.get('PROD_HIERARCHY_1')?.toLowerCase() == "electronics") {
				return "AI Glasses";
			} else if (props.product.productAttributes.get('EXCLUSIVE_GL_US')?.toLowerCase() == "true") {
				return "Exclusive";
			} else if (props.product.productAttributes.get('PROD_HIERARCHY_1')?.toLowerCase() && isCategoryPresent(["gl_newarrivals", "gl_newarrivals_sunglasses", "gl_newarrivals_eyeglasses"], props.product.parentCatalogGroupID)) {
				return "New arrival";
			} else if (isCategoryPresent(["gl_bestsellers", "gl_bestsellers_Sunglasses", "gl_bestsellers_Eyeglasses"], props.product.parentCatalogGroupID)) {
				return "Best Seller";
			}
		};

		const getSecondBadge = () => {
			if (isCategoryPresent(["gl_clearance", "gl_clearance_Sunglasses", "gl_clearance_Eyeglasses"], props.product.parentCatalogGroupID)) {
				return "Discontinued";
			} else if ((props.product.productAttributes.get('BADGE_CHANNEL')?.toLowerCase() != null && props.product.productAttributes.get('BADGE_CHANNEL')?.trim().length != 0) && isCategoryPresent(["gl_newarrivals", "gl_newarrivals_sunglasses", "gl_newarrivals_eyeglasses"], props.product.parentCatalogGroupID)) {
				return "New arrival";
			} else if (store.getters.configurationParams.isSpeedyRxEnabled && props.product.itemAttributes.get('SPEEDYRX_ELIGIBLE_GL') == "true" && store.getters.configurationParams.isSpeedyPLP === false) {
				return "2-DAY SPEEDY DELIVERY";
			} else if (props.product.productAttributes.get('LENS_TREATMENT')?.toLowerCase() == "photochromic") {
				return "Photochromic";
			} else if (props.product.productAttributes.get('POLARIZED')?.toLowerCase() == "true") {
				return "Polarized";
			} else if (props.product.productAttributes.get('BLUE_LIGHT_PROTECTION')?.toLowerCase() == "true") {
				return "Blue Light Filter";
			} else if (props.product.productAttributes.get('GEOFIT')?.toLowerCase() == "universal fit" || props.product.productAttributes.get('GEOFIT')?.toLowerCase() == "universal fitting") {
				return "Universal Fit";
			}
		};

		const getIncludingLensesLabel = () => {
			const completePairCategories = store.getters.configurationParams.completePairMode.split(",");
			if (props.product.productAttributes.get('PRODUCT_TYPE')?.toLowerCase() == "optical" && props.product.productAttributes.get('FRAME_TYPE')?.toLowerCase() != "rimless" && (store.getters.configurationParams.completePairMode == "ENABLED" || !isCategoryPresent(completePairCategories, props.product.parentCatalogGroupID))) {
				return true;
			} else {
				return false;
			}
		};

		const isCategoryPresent = (valuesToCheck: string[], array: string[]): boolean => {
			const map = store.getters.categoriesMap;
			const keys = array.map((item) => item.split("_")[1]); // Estrai la parte dopo "_"

			// Verifica se almeno uno dei valori è presente nella mappa
			return valuesToCheck.some((value) =>
				keys.some((key) => map.get(key)?.toLowerCase() === value.toLowerCase())
			);
		};

		const getProductUrl = async () => {
			const brandMap = store.getters.brandSeoUrlMap;
			const brandKey = props.product.brand;

			try {
				let brandUrl = brandMap[brandKey]; // Controlla se il valore esiste nella mappa

				if (!brandUrl) {
					// Se non esiste, controlla se è in corso una richiesta per lo stesso brandKey
					if (!pendingRequests[brandKey]) {
						// Avvia la richiesta e salvala nella cache temporanea
						pendingRequests[brandKey] = getBrandUrl().then((result) => {
							// Aggiorna la mappa Vuex una volta ottenuto il risultato
							store.commit("SET_BRAND_SEO_URL", { key: brandKey, value: result });
							delete pendingRequests[brandKey]; // Rimuove la richiesta dalla cache
							return result;
						}).catch((error) => {
							delete pendingRequests[brandKey]; // Rimuove la richiesta dalla cache in caso di errore
							throw error;
						});
					}

					// Aspetta il risultato della richiesta in corso
					brandUrl = await pendingRequests[brandKey];
				}

				const element = document.getElementById("plp-app");

				// Genera l'URL del prodotto
				if (brandUrl) {
					var url = `https://${window.location.hostname}${brandUrl}/${props.product.partNumber}`;
					if (element) {
						if (element.getAttribute("data-category-name") === 'gl-speedy-rx') {
							url = url + '?SpeedyChecked=true';
						}
					}
					return url;
				} else {
					var url = `https://${window.location.hostname}/gl-us/eyewear/${props.product.partNumber}`;
					if (element) {
						if (element.getAttribute("data-category-name") === 'gl-speedy-rx') {
							url = url + '?SpeedyChecked=true';
						}
					}
					return url;
				}
			} catch (error) {
				console.error("Error getting product URL: ", error);
				return `https://${window.location.hostname}/gl-us/eyewear/${props.product.partNumber}`; // Fallback URL in caso di errore
			}
		};



		// Funzione per ottenere l'URL del brand
		const getBrandUrl = async () => {
			try {
				return await getBrandSeoUrl("gl_brand_" + props.product.brand);
			} catch (error) {
				console.error("Error getting brand URL: ", error);
				return ""; // Fallback in caso di errore
			}
		};

		// Calcola l'URL del prodotto una volta montato il componente
		onMounted(async () => {
			productUrl.value = await getProductUrl();
		});



		return {
			getFirstImage,
			getSecondImage,
			loadSecondImage,
			secondImageLoaded,
			discountPercentage,
			product: props.product,
			index: props.index,
			firstBadge: getFirstBadge(),
			secondBadge: getSecondBadge(),
			getModelName,
			getIncludingLensesLabel,
			completePairLabel: store.getters.configurationParams.completePairLabel,
			productUrl,
			onFirstImageRemoveLoader,
			onSecondImageRemoveLoader,
			firstImageRemoveLoader,
			secondImageRemoveLoader,
			hasAlternateTransition,
			isFlatPLP: store.getters.configurationParams.isFlatPLP,
			bothImagesLoaded,
			checkImagesLoaded
		};
	},
});